import React, { ReactElement } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WebsiteIntegrationSection } from 'components/SmartAssistantSections/WebsiteIntegrationSection/WebsiteIntegrationSection';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useWebsiteSmartAssistantForms } from 'hooks/SmartAssistantForms/use-website-smart-assistant-forms';
import { useSmartAssistantVisualConfigExtractor } from 'hooks/use-smart-assistant-theme-extractor';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { IntegrationManagerService } from 'services/integration-manager-service';
import { NotificationService } from 'services/notification-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface WebsiteIntegrationManagementSectionProps {
  smartAssistant: SmartAssistant;
  avatar: ReactElement;
}

export function WebsiteIntegrationManagementSection({
  smartAssistant,
  avatar
}: WebsiteIntegrationManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [subscription] = useSubscriptionContext();
  const { websiteIntegrationForm } = useWebsiteSmartAssistantForms(smartAssistant);

  const smartAssistantVisualConfig = useSmartAssistantVisualConfigExtractor({
    smartAssistant,
    avatarUrl: avatar
  });

  const updateIntegrationsMutation = useMutation({
    mutationFn: IntegrationManagerService.updateSmartAssistantIntegrations,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      websiteIntegrationForm.resetDirty();
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_INTEGRATIONS');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_INTEGRATIONS')
  });

  const onSave = async () => {
    const websiteConfigurationValidation = websiteIntegrationForm.validate();
    if (websiteConfigurationValidation.hasErrors) return;

    updateIntegrationsMutation.mutate({
      smartAssistantId: smartAssistant._id,
      websiteIntegration: { ...websiteIntegrationForm.values }
    });
  };

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.WEBSITE_INTEGRATION_SECTION.TITLE')}
      hasChanges={websiteIntegrationForm.isDirty()}
      loading={updateIntegrationsMutation.isLoading}
      onSave={onSave}
      onCancel={() => websiteIntegrationForm.reset()}
      withMarginBottom
      disabled={!subscription.product.smartAssistantIntegrations}>
      <WebsiteIntegrationSection
        form={websiteIntegrationForm}
        smartAssistantVisualConfig={smartAssistantVisualConfig}
        smartAssistantId={smartAssistant._id}
        whiteLabelIntegration={smartAssistant.whiteLabelIntegration}
      />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
